import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import { AiOutlineEye } from "react-icons/ai";
import { HiOutlineDocumentText, HiOutlineDownload } from "react-icons/hi";
import { MdClose } from "react-icons/md";
import { format } from "date-fns";
import { FlexRow, Spacer } from "../../components";
import { IconActionButton, ViewButton } from "../scholarship-applications/ScholarshipApplicationsTable";
import { ROWS_PER_PAGE_OPTIONS } from "../../data/pagination";
import { PaginationProps, TransactionListProps } from "../../typings";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

interface TransactionsTableProps {
  columns: Column[];
  data: TransactionListProps[];
  onView?: (transaction: TransactionListProps) => void;
  pagination: PaginationProps;
  onPageChange: (e: unknown, page: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ReceiptModal = ({ open, onClose, url }: { open: boolean, onClose: () => void, url: string | null }) => {
  const theme = useTheme();
  
  const handleDownload = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: theme.palette.primary.main, color: 'white' }}>
        <Typography variant="h6">Transaction Receipt</Typography>
        <Box>
          <Tooltip title="Download / Print" arrow>
            <IconButton onClick={handleDownload} sx={{ color: 'white', mr: 1 }}>
              <HiOutlineDownload />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <MdClose />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '80vh', overflow: 'hidden' }}>
        {url ? (
          <iframe
            src={url}
            title="Receipt"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>No receipt available.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  columns,
  data,
  onView,
  pagination,
  onPageChange,
  onRowsPerPageChange
}) => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState<string>(columns[0].id);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  
  // State to handle controlled tooltips to avoid them getting stuck
  const [tooltipState, setTooltipState] = useState<{ id: number, type: 'view' | 'receipt' } | null>(null);

  const handleSort =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

  const getFromAccount = (row: TransactionListProps) => row.transaction_lines.find(l => l.transaction_type === "OUT")?.account_name || 'N/A';
  const getToAccount = (row: TransactionListProps) => row.transaction_lines.find(l => l.transaction_type === "IN")?.account_name || 'N/A';

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (orderBy === 'from_account_name') {
        aValue = getFromAccount(a);
        bValue = getFromAccount(b);
      } else if (orderBy === 'to_account_name') {
        aValue = getToAccount(a);
        bValue = getToAccount(b);
      } else if (orderBy === 'scholarship_application_id') {
        aValue = a.transaction_lines.length > 0 ? a.transaction_lines[0].scholarship_application_id : null;
        bValue = b.transaction_lines.length > 0 ? b.transaction_lines[0].scholarship_application_id : null;
      } else {
        aValue = (a as any)[orderBy];
        bValue = (b as any)[orderBy];
      }
      
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, order, orderBy]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getTransactionAmount = (transactionLines: any[]) => {
    return transactionLines.length > 0 ? transactionLines[0].amount : 0;
  };

  const handleViewDetails = async (transaction: TransactionListProps) => {
    setTooltipState(null); // Force close tooltip
    if (onView) {
      setLoadingId(transaction.id);
      try {
        await onView(transaction);
      } finally {
        setLoadingId(null);
      }
    }
  };

  const handleViewReceipt = (url: string | null) => {
    setTooltipState(null); // Force close tooltip
    setReceiptUrl(url);
    setReceiptModalOpen(true);
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 3 }}>
      <TableContainer sx={{ maxHeight: 630 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={handleSort(column.id)}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {column.label}
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  Transaction Amount
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => {
              const transactionAmount = getTransactionAmount(row.transaction_lines);
              
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.colors["white"]["400"],
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  {columns.map((column) => {
                    let value: any;

                    if (column.id === 'from_account_name') {
                      value = getFromAccount(row);
                    } else if (column.id === 'to_account_name') {
                      value = getToAccount(row);
                    } else if (column.id === 'scholarship_application_id') {
                      value = row.transaction_lines.length > 0 ? row.transaction_lines[0].scholarship_application_id : 'N/A';
                    } else {
                      value = (row as any)[column.id];
                    }
                    
                    if (column.id === 'date' && value) {
                      value = format(new Date(value as string), "MMM dd, yyyy");
                    }
                    
                    if (!['from_account_name', 'to_account_name', 'scholarship_application_id'].includes(column.id) && (Array.isArray(value) || typeof value === 'object')) {
                      value = '';
                    }

                    const displayValue = value === null || value === undefined ? 'N/A' : value;
                    
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format ? column.format(displayValue) : displayValue}
                      </TableCell>
                    );
                  })}
                  
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="600" color="primary.main">
                      {formatCurrency(transactionAmount)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <FlexRow justify="center" align="center">
                      <Tooltip 
                        title="View Receipt" 
                        arrow 
                        placement="top"
                        open={tooltipState?.id === row.id && tooltipState?.type === 'receipt'}
                        onOpen={() => setTooltipState({ id: row.id, type: 'receipt' })}
                        onClose={() => setTooltipState(null)}
                      >
                        <span>
                          <IconActionButton
                            onClick={() => handleViewReceipt(row.generated_receipt)}
                            disabled={!row.generated_receipt}
                          >
                            <HiOutlineDocumentText size={18} />
                          </IconActionButton>
                        </span>
                      </Tooltip>
                      <Spacer horizontal={0.5} />
                      <Tooltip 
                        title="View Details" 
                        arrow 
                        placement="top"
                        open={tooltipState?.id === row.id && tooltipState?.type === 'view'}
                        onOpen={() => setTooltipState({ id: row.id, type: 'view' })}
                        onClose={() => setTooltipState(null)}
                      >
                        <span>
                          <IconActionButton
                            onClick={() => handleViewDetails(row)}
                            disabled={loadingId === row.id}
                          >
                            {loadingId === row.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <AiOutlineEye size={18} />
                            )}
                          </IconActionButton>
                        </span>
                      </Tooltip>
                    </FlexRow>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={pagination.total_no_of_records}
        rowsPerPage={pagination.page_size}
        page={pagination.page_number - 1}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
      <ReceiptModal
        open={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        url={receiptUrl}
      />
    </Paper>
  );
};

export default TransactionsTable;
